import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useRef } from 'react';

import { ScrollStoreCtx } from '../../../stores/ScrollStore';
import { black, white } from '../styles/commonColors';
import { getSVG } from '../styles/mixins';
import PageTitleLeftButton from './LeftButton/PageTitleLeftButton';
import styles from './PageTitle.module.scss';
import PageTitleRightButton from './RightButton/PageTitleRightButton';

const historyBack = (): void => {
  history.back();
};

export const Logo = ({ href = '/' }: { href?: string } = {}): JSX.Element => {
  return (
    <a href={href} className={styles.logo}>
      <img
        src={getSVG('/static/images/logo.svg')}
        alt="NOL 웹사이트. 최저가 모텔, 호텔, 종합 숙박, 대한민국 대표 숙소 예약사이트, NOL 숙박 어플"
      />
    </a>
  );
};

export type TPageTitleLeftProps = 'back' | 'close';
export type TPageTitleRightProps = 'search' | 'home' | 'share' | 'close' | 'cart';
export type TPageTitleAlign = 'center' | 'left';

export interface IPageTitleProps {
  color?: 'black' | 'white';
  background?: 'white' | 'transparent' | 'animated';
  /**
   * 우선순위 1. onClickLeft 2. left[something] 3. 뒤로가기
   */
  left?: TPageTitleLeftProps | { [key in TPageTitleLeftProps]?: () => void };
  onClickLeft?: React.MouseEventHandler;
  onBeforeClickLeft?: { [key in TPageTitleLeftProps]?: () => void };
  /**
   * 우선순위 1. onClickRight 2. right[something] 3. search -> /search, home -> /
   */
  right?: TPageTitleRightProps | 'default' | { [key in TPageTitleRightProps]?: () => void };
  onBeforeClickRight?: { [key in TPageTitleRightProps]?: () => void };
  hasBottomBorder?: boolean;
  renderRight?: () => React.ReactElement<any>;
  renderLeft?: () => React.ReactElement<any>;
  className?: string;
  children?: React.ReactNode;
  titleAlign?: TPageTitleAlign;
  cartCount?: number;
}

const PageTitle: React.FC<IPageTitleProps> = ({
  left,
  right,
  color = 'black',
  background = 'white',
  onClickLeft = historyBack,
  onBeforeClickLeft,
  onBeforeClickRight,
  hasBottomBorder = true,
  renderRight,
  renderLeft,
  className,
  children,
  titleAlign = 'center',
  cartCount,
}) => {
  const scrollStore = useContext(ScrollStoreCtx);
  const elRef = useRef<HTMLDivElement>(null);

  const isLeftAlignTitle = titleAlign === 'left';

  useEffect(() => {
    if (elRef?.current) {
      const preventTouchEvent = (e: TouchEvent): void => e.preventDefault();
      elRef.current.addEventListener('touchmove', preventTouchEvent, { passive: false });

      return () => elRef.current?.removeEventListener('touchmove', preventTouchEvent);
    }
  }, []);

  let animatedColor = color;
  const animatedBackgroundHeight = 200;
  const containerStyle: React.CSSProperties = {};
  const titleStyle: React.CSSProperties = {};

  if (background === 'animated') {
    const scrollTop = scrollStore.position.y;
    const alpha = scrollTop > animatedBackgroundHeight ? 1 : scrollTop / animatedBackgroundHeight;
    containerStyle.background = `rgba(255,255,255,${alpha})`;
    titleStyle.color = white;
    titleStyle.opacity = alpha;

    if (alpha > 0.5) {
      animatedColor = 'black';
      titleStyle.color = black;
    }
  }

  return (
    <div
      ref={elRef}
      className={clsx('page-title', styles.container, hasBottomBorder && styles.hasBottomBorder, background, className)}
      style={containerStyle}
    >
      <div className={styles.pageTitleWrap}>
        <div className={clsx(styles.leftWrap, isLeftAlignTitle && styles.pageTitleLeftAlignSideButton)}>
          <PageTitleLeftButton
            color={animatedColor}
            left={left}
            onClickLeft={onClickLeft}
            onBeforeClickLeft={onBeforeClickLeft}
            renderLeft={renderLeft}
          />
        </div>
        <h1
          className={clsx(
            styles.pageTitle,
            right === 'default' && 'with-right-default',
            isLeftAlignTitle && styles.pageTitleLeftAlign,
          )}
          style={titleStyle}
        >
          {children}
        </h1>
        <div className={clsx(styles.rightWrap, isLeftAlignTitle && styles.pageTitleLeftAlignSideButton)}>
          <PageTitleRightButton
            color={animatedColor}
            right={right}
            onBeforeClickRight={onBeforeClickRight}
            renderRight={renderRight}
            cartCount={cartCount}
          />
        </div>
      </div>
    </div>
  );
};

export default observer(PageTitle);
